<template>
  <div v-if="teamPathways" class="select-pathway">
    <div class="row">
      <div class="col-md-12 text-center">
        <h2 class="choose-pathway" data-test="choose-pathway-title">{{ headerText }}</h2>
      </div>
    </div>
    <div class="row pathway-tiles" v-if="!isExpandedView" data-test="pathway-grid">
      <div class="pathway-tile" v-for="(pathway, index) in pathways" :key="index">
        <PathwayTile
          :data-test="`${pathway.pathwayUid}`"
          :pathway="pathway"
          :isExpandedView="isExpandedView"
          @toggle-expanded-view="toggleExpandedView"
        />
      </div>
    </div>
    <div class="row pathway-carousel expanded-tiles" v-else>
      <Carousel
        icon-control-left="btn btn-default-fab-chevron left"
        icon-control-right="btn btn-default-fab-chevron right"
        :interval="0"
        :indicators="false"
        :controls="controls"
      >
        <Slide v-for="(pathway, index) in pathways" :key="index">
          <PathwayTile
            :data-test="`${pathway.pathwayUid}-expanded`"
            :pathway="pathway"
            :isExpandedView="isExpandedView"
            @toggle-expanded-view="toggleExpandedView"
          />
        </Slide>
      </Carousel>
    </div>
    <section
      v-if="!isTeamLeader && !isAccreditedUser && isExpandedView"
      class="team-member-message"
      data-test="team-member-message"
    >
      <p>
        Only your Team Lead can initiate an Outcome and Pathway for your team. Let them know which
        one you'd like to pick!
      </p>
      <div class="admin-question">
        <p><b>Not sure who your Team Lead is? </b></p>
        <img src="../../assets/leader-crown-icon.png" alt="team lead icon" />
      </div>
      <p>
        Check out the
        <a :href="`/team/members/${teamId}`"> Team members page </a> on your Team dashboard and look
        for the person with a crown beside their name.
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { inject, computed, ref, Ref, onMounted } from "vue";
import { Carousel, Slide, Btn } from "uiv";
import { useRoute } from "vue-router";
import PathwayTile from "./pathway-tile.vue";
import { CmsPathwayData, CmsPathwaysRelationship } from "../../services/cms/cms-type-definitions";
import { Analytics } from "../../services/analytics/analytics";
import cms from "../../services/cms/cms";
import { csContext } from "../../services/cs-context";
import axios from 'axios';

export interface PathwayTileData {
  pathwayId: string;
  pathwayUid: string;
  data: Partial<CmsPathwayData>;
}

const teamPathways = inject("teamPathways") as Ref<CmsPathwaysRelationship[]>;

const props = defineProps<{
  isTeamLeaderOrOrgAdmin: boolean;
  teamId: string;
  orgId: string;
}>();

const emit = defineEmits(["displayNextButton"]);

const isExpandedView = ref<boolean>(false);

const activePathway = ref<PathwayTileData | null>(null);

const carouselPathways = ref<PathwayTileData[] | null>(null);

const controls = ref(true);

const route = useRoute();

const isAccreditedUser = computed(() => route.query.asaccredited === 'true');

const isTeamLeader = computed(() => {
  return props.isTeamLeaderOrOrgAdmin;
});

const isOrgAdmin = computed(() => {
  return props.isTeamLeaderOrOrgAdmin;
});

const handleDisplayControls = (pathways: PathwayTileData[]): void => {
  controls.value = pathways.length > 1;
};

const mapToData = (p: CmsPathwaysRelationship): PathwayTileData => {
  console.log('Mapping pathway:', p);
  return {
    pathwayUid: p.pathway.uid,
    pathwayId: p.pathway.id,
    data: p.pathway.data ?? {},
  };
};

const preparePathways = (): PathwayTileData[] => {
  console.log('Preparing pathways from:', teamPathways.value);
  return teamPathways.value.map((p) => mapToData(p));
};

const pathways = computed(() => {
  const preparedPathways = preparePathways();
  console.log('Prepared pathways:', preparedPathways);
  if (isExpandedView.value && carouselPathways.value) {
    handleDisplayControls(carouselPathways.value);
    return carouselPathways.value;
  }
  return preparedPathways;
});

const headerText = computed(() => {
  if (isTeamLeader.value || isOrgAdmin.value || isAccreditedUser.value) {
    return "Choose a pathway";
  }
  return "Browse pathways";
});

const teamId = computed(() => route.params.id);

const currentIndex = computed(() => {
  const pathwayTitle = activePathway.value?.data.title;
  const preparedPathways = preparePathways();
  return preparedPathways.findIndex((pathway) => {
    return pathway.data.title === pathwayTitle;
  });
});

const getCarouselOrder = (): void => {
  const pathwayIndex = currentIndex.value;
  const preparedPathways = preparePathways();
  const firstSection = preparedPathways.slice(pathwayIndex);
  const secondSection = preparedPathways.slice(0, pathwayIndex);
  carouselPathways.value = firstSection.concat(secondSection);
};

const toggleExpandedView = ({
  pathway,
  isOpen,
}: {
  pathway: PathwayTileData;
  isOpen: boolean;
}): void => {
  isExpandedView.value = isOpen;
  activePathway.value = pathway;
  if (isOpen) {
    getCarouselOrder();
  }
  Analytics.action("Clicked Team Pathway", "TeamPathwayActions", {
    outcomeTitle: cms.asText(activePathway.value?.data.goal_relationship?.data?.full_title),
    outcomeUid: activePathway.value?.data.goal_relationship?.uid,
    outcomeId: activePathway.value?.data.goal_relationship?.id,
    pathwayId: activePathway.value?.pathwayId,
    pathwayName: cms.asText(activePathway.value?.data?.title),
  });
  emit("displayNextButton", activePathway.value?.pathwayUid ?? "");
};
</script>

<style scoped lang="less">
@import "../../styles/outcomes/select-pathway";
</style>

<style lang="less">
@import "../../styles/outcomes/prismic-select-pathway";
</style>
